import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { vertexShader, fragmentShader } from "./shaders";

export class Surface {
    constructor(popup, container, globeCanvas, globeOverlayCanvas) {
        this.popup = popup;
        this.container = container;
        this.globeCanvas = globeCanvas;
        this.globeOverlayCanvas = globeOverlayCanvas;
        this.renderer = new THREE.WebGLRenderer({ canvas: this.globeCanvas, alpha: true });
        this.scene = new THREE.Scene();

        this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 2);
        this.camera.position.z = 1.1;
        this.updateSize();

        this.rayCaster = new THREE.Raycaster();
        this.rayCaster.far = 1.15;
        this.mouse = new THREE.Vector2();
        this.coordinates2D = [0, 0];

        this.setupOverlayGraphic();
        this.createOrbitControls();

        this.clock = new THREE.Clock();
        this.clickTime = 0;

        this.group = new THREE.Group();
        this.group.scale.setScalar(0.43);
        this.scene.add(this.group);

        this.selectionVisible = false;
    }

    createOrbitControls() {
        this.controls = new OrbitControls(this.camera, this.globeCanvas);
        this.controls.enabled = false;
        this.controls.minPolarAngle = 0.4 * Math.PI;
        this.controls.maxPolarAngle = 0.4 * Math.PI;
        this.controls.autoRotate = true;
    }

    createGlobe() {
        const globeGeometry = new THREE.IcosahedronGeometry(1, 15);
        this.mapMaterial = new THREE.ShaderMaterial({
            vertexShader,
            fragmentShader,
            uniforms: {
                u_visibility: { type: "t", value: this.earthTexture },
                u_size: { type: "f", value: 0.5 },
                u_color_main: { type: "v3", value: new THREE.Color(0xffffff) },
                u_glow_color: { type: "v3", value: new THREE.Color(0x0066ff) },
                u_clicked: { type: "v3", value: new THREE.Vector3(0.0, 0.0, 1) },
                u_time_since_click: { value: 3 },
            },
            alphaTest: false,
            transparent: true,
        });

        const globe = new THREE.Points(globeGeometry, this.mapMaterial);
        this.group.add(globe);

        this.blackGlobe = new THREE.Mesh(
            globeGeometry,
            new THREE.MeshBasicMaterial({
                color: 0x2c2c2e,
                transparent: true,
                opacity: 0.1,
            })
        );
        this.blackGlobe.scale.setScalar(0.99);
        this.group.add(this.blackGlobe);
    }

    addAnchor() {
        const geometry = new THREE.SphereGeometry(0.02, 16, 16);
        const material = new THREE.MeshBasicMaterial({
            color: 0x000741,
            transparent: true,
            opacity: 1,
        });
        this.anchor = new THREE.Mesh(geometry, material);
        this.group.add(this.anchor);
    }

    setupOverlayGraphic() {
        this.overlayCtx = this.globeOverlayCanvas.getContext("2d");
        this.overlayCtx.strokeStyle = "#4BC0C8";
        this.overlayCtx.lineWidth = 5;
        this.overlayCtx.lineCap = "round";
    }

    getImageData() {
        const image = this.earthTexture.image;
        const canvas = document.createElement("canvas");
        canvas.width = image.width;
        canvas.height = image.height;
        const context = canvas.getContext("2d");
        context.drawImage(image, 0, 0);
        return context.getImageData(0, 0, image.width, image.height);
    }

    getLatLong() {
        const pos = this.anchor.position;
        const lat = 90 - (Math.acos(pos.y) * 180) / Math.PI;
        const lng = ((270 + (Math.atan2(pos.x, pos.z) * 180) / Math.PI) % 360) - 180;
        return lat.toFixed(6) + ",&nbsp;" + lng.toFixed(6);
    }

    render() {
        this.mapMaterial.uniforms.u_time_since_click.value = this.clock.getElapsedTime() - this.clickTime;
        this.controls.update();
        this.renderer.render(this.scene, this.camera);
    }

    loop() {
        this.render();
        requestAnimationFrame(this.loop.bind(this));
    }

    updateSize() {
        const minSide = 0.85 * Math.min(window.innerWidth, window.innerHeight);
        this.container.style.width = minSide + "px";
        this.container.style.height = minSide + "px";
        this.renderer.setSize(minSide, minSide);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        this.camera.updateProjectionMatrix();
        this.globeOverlayCanvas.width = minSide;
        this.globeOverlayCanvas.height = minSide;
    }

    updateDotSize() {
        this.mapMaterial.uniforms.u_size.value = 0.003 * this.container.offsetWidth;
    }
}
